import { useCallback, useEffect, useMemo, useState } from 'react';

import { useUI } from '@/hooks/ui/useUI';
import { useTranslation } from '@/i18n';
import { monthlyCutsService } from '@/api/domains/attendance';
import type { MonthlyCutGroup } from '@/api/domains/attendance';


// pageSize máximo aceptado por el backend; las agrupaciones se cargan de una vez
// (el dominio securityGrouping ya usa este tamaño para listar agrupaciones).
const GROUPS_PAGE_SIZE = 1000;

export interface UseMonthlyCutGroupsTransferOptions {
  // Cuando es false no se cargan las agrupaciones (gate de permiso, igual que `enabled`
  // en usePaginatedList). La carga se dispara automáticamente al pasar a true. Default: true.
  enabled?: boolean;
  // Id del corte en edición. Cuando se entrega, la lista derecha arranca con las agrupaciones
  // ya asignadas (getAssignedGroups) y la izquierda con las disponibles excluyendo las asignadas
  // (getAvailableGroups las filtra por monthlyCutId). En modo crear se omite.
  monthlyCutId?: number;
}

export interface UseMonthlyCutGroupsTransferResult {
  isLoading: boolean;

  // Panel izquierdo: agrupaciones disponibles (sin asignar)
  availableAll: MonthlyCutGroup[];
  filteredAvailable: MonthlyCutGroup[];
  availableSearch: string;
  onAvailableSearch: (text: string) => void;
  selectedAvailableIds: Set<number>;
  onToggleAvailable: (id: number) => void;
  onSelectAllAvailable: () => void;
  isAllAvailableSelected: boolean;
  isAvailableIndeterminate: boolean;
  onDoubleClickAvailable: (item: MonthlyCutGroup) => void;

  // Panel derecho: agrupaciones asignadas
  assigned: MonthlyCutGroup[];
  filteredAssigned: MonthlyCutGroup[];
  assignedSearch: string;
  onAssignedSearch: (text: string) => void;
  selectedAssignedIds: Set<number>;
  onToggleAssigned: (id: number) => void;
  onSelectAllAssigned: () => void;
  isAllAssignedSelected: boolean;
  isAssignedIndeterminate: boolean;
  onDoubleClickAssigned: (item: MonthlyCutGroup) => void;

  onAdd: () => void;
  onRemove: () => void;

  // IDs de las agrupaciones asignadas (se persisten vía groupIds al crear el corte)
  assignedIds: number[];
}

function matchesSearch(group: MonthlyCutGroup, query: string): boolean {
  const trimmed = query.trim().toLowerCase();
  if (!trimmed) return true;
  return group.name.toLowerCase().includes(trimmed);
}

/**
 * Maneja la pestaña Agrupación de la creación/edición de un corte. En un corte nuevo no hay
 * monthlyCutId, por lo que la lista de asignadas arranca vacía y el movimiento entre paneles
 * es estado local del frontend (no hay persistencia hasta crear el corte). En edición se
 * entrega monthlyCutId: la lista de asignadas se precarga con las agrupaciones actuales del
 * corte y las disponibles con el resto. En ambos modos el estado posterior es local hasta guardar.
 */
export function useMonthlyCutGroupsTransfer(
  options: UseMonthlyCutGroupsTransferOptions = {},
): UseMonthlyCutGroupsTransferResult {
  const { enabled = true, monthlyCutId } = options;
  const { t: translate } = useTranslation();
  const { showApiError } = useUI();

  const [availableAll, setAvailableAll] = useState<MonthlyCutGroup[]>([]);
  const [assigned, setAssigned] = useState<MonthlyCutGroup[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  const [availableSearch, setAvailableSearch] = useState('');
  const [assignedSearch, setAssignedSearch] = useState('');
  const [selectedAvailableIds, setSelectedAvailableIds] = useState<Set<number>>(new Set());
  const [selectedAssignedIds, setSelectedAssignedIds] = useState<Set<number>>(new Set());

  // Carga todas las agrupaciones de un endpoint paginando hasta agotar el total del servidor.
  // Devuelve null si la petición fue abortada o falló (el caller decide cómo reaccionar).
  const loadAllGroupPages = useCallback(
    async (
      fetchPage: (params: {
        monthlyCutId?: number;
        page: number;
        pageSize: number;
        orderDirection: 'ASC' | 'DESC';
      }, signal: AbortSignal) => ReturnType<typeof monthlyCutsService.getAvailableGroups>,
      signal: AbortSignal,
    ): Promise<MonthlyCutGroup[] | null> => {
      const accumulated: MonthlyCutGroup[] = [];
      let page = 1;
      let totalRecords = Number.POSITIVE_INFINITY;

      while (accumulated.length < totalRecords) {
        const response = await fetchPage(
          { monthlyCutId, page, pageSize: GROUPS_PAGE_SIZE, orderDirection: 'ASC' },
          signal,
        );

        if (response.error?.code === 'ABORTED') return null;

        if (!response.success || !response.data) {
          showApiError(response, translate('attendance:monthlyCutCreate.messages.groupsLoadError'));
          return null;
        }

        accumulated.push(...response.data.items);
        totalRecords = response.data.totalRecords;
        if (response.data.items.length === 0) break;
        page += 1;
      }

      return accumulated;
    },
    [monthlyCutId, showApiError, translate],
  );

  // Carga las agrupaciones disponibles y, en edición (monthlyCutId presente), las asignadas.
  useEffect(() => {
    if (!enabled) return;
    const controller = new AbortController();

    const loadGroups = async () => {
      setIsLoading(true);

      const available = await loadAllGroupPages(
        (params, signal) => monthlyCutsService.getAvailableGroups(params, signal),
        controller.signal,
      );
      if (available === null) {
        if (!controller.signal.aborted) setIsLoading(false);
        return;
      }

      let assignedGroups: MonthlyCutGroup[] = [];
      if (monthlyCutId !== undefined) {
        const result = await loadAllGroupPages(
          (params, signal) => monthlyCutsService.getAssignedGroups(params, signal),
          controller.signal,
        );
        if (result === null) {
          setAvailableAll(available);
          if (!controller.signal.aborted) setIsLoading(false);
          return;
        }
        assignedGroups = result;
      }

      setAvailableAll(available);
      setAssigned(assignedGroups);
      setIsLoading(false);
    };

    void loadGroups();
    return () => controller.abort();
  }, [enabled, monthlyCutId, loadAllGroupPages]);

  const filteredAvailable = useMemo(
    () => availableAll.filter((group) => matchesSearch(group, availableSearch)),
    [availableAll, availableSearch],
  );

  const filteredAssigned = useMemo(
    () => assigned.filter((group) => matchesSearch(group, assignedSearch)),
    [assigned, assignedSearch],
  );

  const onAvailableSearch = useCallback((text: string) => setAvailableSearch(text), []);
  const onAssignedSearch = useCallback((text: string) => setAssignedSearch(text), []);

  const onToggleAvailable = useCallback((id: number) => {
    setSelectedAvailableIds((previous) => {
      const next = new Set(previous);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }, []);

  const onToggleAssigned = useCallback((id: number) => {
    setSelectedAssignedIds((previous) => {
      const next = new Set(previous);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }, []);

  const isAllAvailableSelected = useMemo(
    () =>
      filteredAvailable.length > 0 &&
      filteredAvailable.every((group) => selectedAvailableIds.has(group.id)),
    [filteredAvailable, selectedAvailableIds],
  );

  const isAvailableIndeterminate = useMemo(
    () =>
      !isAllAvailableSelected &&
      filteredAvailable.some((group) => selectedAvailableIds.has(group.id)),
    [filteredAvailable, selectedAvailableIds, isAllAvailableSelected],
  );

  const isAllAssignedSelected = useMemo(
    () =>
      filteredAssigned.length > 0 &&
      filteredAssigned.every((group) => selectedAssignedIds.has(group.id)),
    [filteredAssigned, selectedAssignedIds],
  );

  const isAssignedIndeterminate = useMemo(
    () =>
      !isAllAssignedSelected &&
      filteredAssigned.some((group) => selectedAssignedIds.has(group.id)),
    [filteredAssigned, selectedAssignedIds, isAllAssignedSelected],
  );

  const onSelectAllAvailable = useCallback(() => {
    setSelectedAvailableIds((previous) => {
      const next = new Set(previous);
      if (isAllAvailableSelected) {
        filteredAvailable.forEach((group) => next.delete(group.id));
      } else {
        filteredAvailable.forEach((group) => next.add(group.id));
      }
      return next;
    });
  }, [filteredAvailable, isAllAvailableSelected]);

  const onSelectAllAssigned = useCallback(() => {
    setSelectedAssignedIds((previous) => {
      const next = new Set(previous);
      if (isAllAssignedSelected) {
        filteredAssigned.forEach((group) => next.delete(group.id));
      } else {
        filteredAssigned.forEach((group) => next.add(group.id));
      }
      return next;
    });
  }, [filteredAssigned, isAllAssignedSelected]);

  const onAdd = useCallback(() => {
    if (selectedAvailableIds.size === 0) return;
    setAssigned((previous) => [
      ...previous,
      ...availableAll.filter((group) => selectedAvailableIds.has(group.id)),
    ]);
    setAvailableAll((previous) => previous.filter((group) => !selectedAvailableIds.has(group.id)));
    setSelectedAvailableIds(new Set());
  }, [availableAll, selectedAvailableIds]);

  const onRemove = useCallback(() => {
    if (selectedAssignedIds.size === 0) return;
    setAvailableAll((previous) => [
      ...previous,
      ...assigned.filter((group) => selectedAssignedIds.has(group.id)),
    ]);
    setAssigned((previous) => previous.filter((group) => !selectedAssignedIds.has(group.id)));
    setSelectedAssignedIds(new Set());
  }, [assigned, selectedAssignedIds]);

  const onDoubleClickAvailable = useCallback((item: MonthlyCutGroup) => {
    setAssigned((previous) => [...previous, item]);
    setAvailableAll((previous) => previous.filter((group) => group.id !== item.id));
    setSelectedAvailableIds((previous) => {
      const next = new Set(previous);
      next.delete(item.id);
      return next;
    });
  }, []);

  const onDoubleClickAssigned = useCallback((item: MonthlyCutGroup) => {
    setAvailableAll((previous) => [...previous, item]);
    setAssigned((previous) => previous.filter((group) => group.id !== item.id));
    setSelectedAssignedIds((previous) => {
      const next = new Set(previous);
      next.delete(item.id);
      return next;
    });
  }, []);

  const assignedIds = useMemo(() => assigned.map((group) => group.id), [assigned]);

  return {
    isLoading,
    availableAll,
    filteredAvailable,
    availableSearch,
    onAvailableSearch,
    selectedAvailableIds,
    onToggleAvailable,
    onSelectAllAvailable,
    isAllAvailableSelected,
    isAvailableIndeterminate,
    onDoubleClickAvailable,
    assigned,
    filteredAssigned,
    assignedSearch,
    onAssignedSearch,
    selectedAssignedIds,
    onToggleAssigned,
    onSelectAllAssigned,
    isAllAssignedSelected,
    isAssignedIndeterminate,
    onDoubleClickAssigned,
    onAdd,
    onRemove,
    assignedIds,
  };
}

export default useMonthlyCutGroupsTransfer;
